import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createForm } from '@formily/core';
import SchemaFormAutoSubmit from '@/components/Schema/SchemaFormAutoSubmit';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ServiceDeskAPIService, {
  ServiceDeskAPI,
  agentEmployeeListQueryKey,
} from '@sd/services/APIs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Search, Plus, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { TablePager } from "@sd/components/common/TablePager";
import type { CompanyEmployeeRole } from "@sd/types/admin";

const roleLabel: Record<CompanyEmployeeRole, string> = {
  support_agent: 'Support Agent',
  end_user: 'End User',
};

const roleBadgeClass: Record<CompanyEmployeeRole, string> = {
  support_agent:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/35 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  end_user:
    'bg-blue-100 text-blue-800 dark:bg-blue-900/35 dark:text-blue-300 border-blue-200 dark:border-blue-800',
};

const emptyForm = {
  name: '',
  email: '',
  role: 'end_user' as CompanyEmployeeRole,
  department: '',
  phone: '',
};

/**
 * Phone validation — allows international formats.
 * Valid:  +91 9876543210 | 9876543210 | +1-555-000-0000 | (555) 123-4567
 * Rules:  only digits, +, -, (, ), spaces allowed
 *         min 7 digits, max 15 digits (E.164 standard)
 */
function validatePhone(value: string): string | null {
  if (!value.trim()) return null; // optional field
  if (/[a-zA-Z]/.test(value)) return 'Phone number cannot contain letters';
  if (/[^0-9+\-().\s]/.test(value)) return 'Only digits, +, -, (, ) are allowed';
  const digitsOnly = value.replace(/\D/g, '');
  if (digitsOnly.length < 7) return 'Phone number must have at least 7 digits';
  if (digitsOnly.length > 15) return 'Phone number cannot exceed 15 digits';
  return null;
}

export function AdminEmployees() {
  const queryClient = useQueryClient();
  const schemaName = "ServiceDesk Add Employee";

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const refreshEmployeeCaches = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["admin-employees-list"] });
    void queryClient.invalidateQueries({ queryKey: agentEmployeeListQueryKey });
    void queryClient.invalidateQueries({ queryKey: ["service-desk", "employee_list"] });
    void queryClient.invalidateQueries({ queryKey: ["sd-employee-list"] });
  }, [queryClient]);

  const { data: employeesResponse, isLoading } = useQuery({
    queryKey: ["admin-employees-list", page, pageSize, debouncedSearch],
    queryFn: () => {
      const params: Record<string, unknown> = { page, page_size: pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      return ServiceDeskAPI.employees.list(params).then(r => r.data);
    },
    staleTime: 60_000,
    retry: 1,
  });

  const { data: rolesResponse } = useQuery({
    queryKey: ['sd-roles-list'],
    queryFn: () =>
      ServiceDeskAPI.roles.list({ page: 1, page_size: 100, is_servicedesk: true }).then(r => r.data),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const roleOptions = useMemo(() => {
    const list: any[] = rolesResponse?.results ?? rolesResponse ?? [];
    if (!list.length) return [];
    return list.map((r: any) => ({
      label: r.name,
      value: r.id,
    }));
  }, [rolesResponse]);

  const totalCount: number = employeesResponse?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const rows = useMemo(() => {
    const list: any[] = employeesResponse?.employees ?? employeesResponse?.results ?? employeesResponse ?? [];
    if (!list.length) return [];
    return list.map((item: any) => ({
      id: item.id,
      firstname: item.firstname ?? '',
      lastname: item.lastname ?? '',
      fullName: `${item.firstname ?? ''} ${item.lastname ?? ''}`.trim() || 'Unknown',
      email: item.emailaddress ?? '—',
      designation: item.designation_name ?? '—',
      servicedesk_role: item.servicedesk_role ?? '',
      servicedesk_role_name: item.servicedesk_role_name ?? '—',
      active: item.is_active !== undefined ? item.is_active : true,
    }));
  }, [employeesResponse]);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  // Debounce search → pass to API, reset to page 1
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState<any | null>(null);

  const [addFormData, setAddFormData] = useState({ name: '', email: '', role: '', department: '', phone: '' });
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const formEdit = useMemo(() => createForm({}), []);
  const formEditRef = useRef<{ handleSave: () => Promise<void> }>(null);

  const stats = useMemo(() => {
    const withRole = rows.filter(r => r.servicedesk_role).length;
    const withoutRole = rows.filter(r => !r.servicedesk_role).length;
    const active = rows.filter(r => r.active).length;
    return { total: totalCount, withRole, withoutRole, active };
  }, [rows, totalCount]);

  // roleFilter is client-side only (no API support); search is server-side
  const filtered = roleFilter === 'all'
    ? rows
    : rows.filter(r => r.servicedesk_role === roleFilter);

  // Reset to page 1 when pageSize changes
  useEffect(() => { setPage(1); }, [pageSize]);

  const setRole = async (empId: string, servicedeskRoleId: string) => {
    try {
      await ServiceDeskAPI.employees.update({ emp_id: empId, servicedesk_role: servicedeskRoleId });
      refreshEmployeeCaches();
      toast.success('Role updated');
    } catch (e) {
      toast.error('Failed to update role');
    }
  };

  const remove = async (id: string) => {
    try {
      await ServiceDeskAPIService.formily.deleteItem(schemaName, id);
      refreshEmployeeCaches();
      toast.success('Employee removed');
    } catch (e) {
      toast.error('Failed to delete employee');
    }
  };

  const openAdd = () => {
    setAddFormData({ name: '', email: '', role: '', department: '', phone: '' });
    setPhoneError(null);
    setAddOpen(true);
  };

  const saveAdd = async () => {
    if (!addFormData.name.trim() || !addFormData.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    const phonErr = validatePhone(addFormData.phone);
    if (phonErr) {
      setPhoneError(phonErr);
      return;
    }
    try {
      const response = await ServiceDeskAPIService.formily.createItem(schemaName, addFormData);
      if (response?.id) {
        refreshEmployeeCaches();
        toast.success('Employee added');
        setAddOpen(false);
        setPhoneError(null);
        setAddFormData({ name: '', email: '', role: '', department: '', phone: '' });
      } else {
        toast.error("Failed to get Employee ID after creation");
      }
    } catch (error) {
      toast.error("Please fill in all required fields correctly");
    }
  };

  const openEdit = (r: any) => {
    setEditRow(r);
    formEdit.setInitialValues({
      id: r.id,
      ...r,
    });
  };

  const saveEdit = async () => {
    if (!editRow) return;
    try {
      await formEdit.validate();
      await formEditRef.current?.handleSave();
      
      refreshEmployeeCaches();
      toast.success('Employee updated');
      setEditRow(null);
    } catch (error) {
      toast.error("Please fill in all required fields correctly");
    }
  };

  return (
    <div className="min-w-0 p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground">Employees</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Add company employees and assign their role: End User or Support Agent.
          </p>
        </div>

      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Total employees
            </p>
            <p className="text-2xl font-bold tabular-nums">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              With SD Role
            </p>
            <p className="text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
              {stats.withRole}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Without Role
            </p>
            <p className="text-2xl font-bold tabular-nums text-blue-600 dark:text-blue-400">
              {stats.withoutRole}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Active</p>
            <p className="text-2xl font-bold tabular-nums">{stats.active}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative min-w-0 w-full flex-1 sm:min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="w-full pl-9"
            placeholder="Search by name, email or designation…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {roleOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border bg-card">
        <Table className="min-w-[640px]">
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50 border-b border-border">
              <TableHead className="text-xs font-bold uppercase tracking-wide text-foreground">Name</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide text-foreground">Email</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide text-foreground">Designation</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide text-foreground">SD Role</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                  Loading employees...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                  No employees match your filters.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="max-w-[160px] font-medium text-foreground">
                    <span className="block truncate" title={r.fullName}>{r.fullName}</span>
                  </TableCell>
                  <TableCell className="max-w-[180px] text-muted-foreground">
                    <span className="block truncate" title={r.email}>{r.email}</span>
                  </TableCell>
                  <TableCell className="max-w-[140px] text-muted-foreground">
                    <span className="block truncate" title={r.designation}>{r.designation}</span>
                  </TableCell>
                  <TableCell className="min-w-[190px]">
                    <Select
                      value={r.servicedesk_role ?? ''}
                      onValueChange={v => setRole(r.id, v)}
                    >
                      <SelectTrigger className="h-8 w-full min-w-[160px] max-w-[190px] border font-medium text-xs">
                        <SelectValue placeholder="Assign role…" />
                      </SelectTrigger>
                      <SelectContent>
                        {roleOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="overflow-x-auto">
        <TablePager
          pageNum={page}
          totalPages={totalPages}
          pageSize={pageSize}
          isLoading={isLoading}
          onPageChange={setPage}
          onPageSizeChange={size => { setPageSize(size); setPage(1); }}
        />
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="w-[calc(100%-2rem)] gap-0 overflow-hidden rounded-2xl p-0 shadow-xl sm:max-w-[520px]">
          <DialogHeader className="border-b bg-muted/30 px-4 pb-4 pt-6 text-left sm:px-7 sm:pt-7">
            <div className="flex items-center gap-3 mb-1">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
                <Plus className="h-5 w-5 text-primary" />
              </div>
              <DialogTitle className="text-xl font-bold tracking-tight">Add Employee</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed pl-12">
              Add a new employee and assign them as End User or Support Agent.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="add-name">Full Name <span className="text-destructive">*</span></Label>
                <Input
                  id="add-name"
                  placeholder="e.g. John Smith"
                  value={addFormData.name}
                  onChange={e => setAddFormData(p => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="add-email">Email <span className="text-destructive">*</span></Label>
                <Input
                  id="add-email"
                  type="email"
                  placeholder="e.g. john@company.com"
                  value={addFormData.email}
                  onChange={e => setAddFormData(p => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select
                    value={addFormData.role}
                    onValueChange={v => setAddFormData(p => ({ ...p, role: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.length > 0 ? (
                        roleOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))
                      ) : (
                        <SelectItem value="__loading" disabled>Loading roles...</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="add-dept">Department</Label>
                  <Input
                    id="add-dept"
                    placeholder="e.g. Engineering"
                    value={addFormData.department}
                    onChange={e => setAddFormData(p => ({ ...p, department: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="add-phone">Phone</Label>
                <Input
                  id="add-phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="e.g. +91 98765 43210"
                  value={addFormData.phone}
                  onChange={e => {
                    const val = e.target.value;
                    setAddFormData(p => ({ ...p, phone: val }));
                    setPhoneError(validatePhone(val));
                  }}
                  className={cn(phoneError && 'border-destructive focus-visible:ring-destructive')}
                />
                {phoneError ? (
                  <p className="text-xs text-destructive">{phoneError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Format: +91 XXXXX XXXXX or 10-digit number
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 border-t bg-muted/20 px-4 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-7 sm:py-5">
            <Button variant="outline" onClick={() => setAddOpen(false)} className="min-w-[96px] w-full rounded-lg sm:w-auto">
              Cancel
            </Button>
            <Button onClick={saveAdd} className="min-w-[130px] w-full gap-2 rounded-lg sm:w-auto">
              <Plus className="h-4 w-4" />
              Add Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRow} onOpenChange={o => !o && setEditRow(null)}>
        <DialogContent className="w-[calc(100%-2rem)] gap-0 overflow-hidden rounded-2xl p-0 shadow-xl sm:max-w-[520px]">
          <DialogHeader className="border-b bg-muted/30 px-4 pb-4 pt-6 text-left sm:px-7 sm:pt-7">
            <div className="flex items-center gap-3 mb-1">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10">
                <Pencil className="h-4 w-4 text-amber-600" />
              </div>
              <DialogTitle className="text-xl font-bold tracking-tight">Edit Employee</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed pl-12">
              Update details for <span className="font-medium text-foreground">{editRow?.name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] space-y-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
            <SchemaFormAutoSubmit
              form={formEdit}
              schemaName={schemaName}
              recordId={editRow?.id}
              ref={formEditRef}
            />
          </div>
          <DialogFooter className="flex-col gap-2 border-t bg-muted/20 px-4 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-7 sm:py-5">
            <Button variant="outline" onClick={() => setEditRow(null)} className="min-w-[96px] w-full rounded-lg sm:w-auto">
              Cancel
            </Button>
            <Button onClick={saveEdit} className="min-w-[130px] w-full rounded-lg sm:w-auto">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
